import type { DateTime } from "luxon";
import type { DropdownItem } from "../../../../../components/UIToolkit/Inputs/SystemDropDown";

/**
 * Wire types for the support-only schedule-push panel (`/departments/{id}/schedule-push/{source}`), mirroring
 * `backend/models/schedule_push.py`. Every ISO date and datetime arrives as a Luxon `DateTime`: `axiosInstance`
 * converts them on the way in.
 */

/** The vendors a schedule can be pushed to. The value is the backend's `ExternalSource` (HostedTime is `hostdime`). */
export enum SchedulePushSource {
	HostedTime = "hostdime",
	AttendanceOnDemand = "aod",
}

/** How the vendor decides where a department's shifts are filed. */
export enum SchedulePushMappingMode {
	/** Each location is mapped to one of the facility's units (HostedTime). */
	LocationMap = "location_map",
	/** Nothing to map: the facility key alone decides it (Attendance on Demand's workgroups). */
	None = "none",
}

/** Which Attendance on Demand business unit a department's shifts are filed under — the run's own rule, never re-derived here. */
export enum SchedulePushWorkgroupRule {
	/** The tenant doesn't split locations into business units: every shift under the location alone. */
	LocationOnly = "location_only",
	/** The key names one business unit: every shift under it. */
	SingleBusinessUnit = "single_bu",
	/** The key names several: each shift under its employee's home unit when that is one of them; others aren't sent. */
	HomeBusinessUnitWithin = "home_bu_within",
	/** The key names the location alone: each shift under its employee's home unit, for staff based at the location. */
	HomeBusinessUnitAtLocation = "home_bu_at_location",
}

export enum SchedulePushLoginStatus {
	Configured = "configured",
	NotConfigured = "not_configured",
	/** Some of the login's settings are set and some are not; the scheduled run skips the department exactly as when none are. */
	PartiallyConfigured = "partially_configured",
}

/** How the scheduled run would file a location's shifts — decided by the run's own classifier, never re-derived here. */
export enum SchedulePushLocationStatus {
	Mapped = "mapped",
	MatchedBySpelling = "matched_by_spelling",
	NotMapped = "not_mapped",
	NoLocationDefault = "no_location_default",
	NoLocationUnmapped = "no_location_unmapped",
}

export enum SchedulePushPreviewStatus {
	Running = "running",
	Succeeded = "succeeded",
	Failed = "failed",
	/** A poll found it still running past the cutoff: the job died (a deploy, a crash). */
	Stale = "stale",
}

export interface SchedulePushTarget {
	facilityCode: string;
	active: boolean;
	updatedBy: string;
	updatedAt: DateTime;
}

export interface SchedulePushMapRow {
	/** Null is the row for shifts with no location. */
	location: string | null;
	unitName: string;
}

export interface SchedulePushLocationRow {
	location: string;
	/** Published shifts at this exact spelling inside the push window. */
	shiftCount: number;
	status: SchedulePushLocationStatus;
	/** True when a shift template names this location; false for a spelling only on shifts or only in the map. */
	configured: boolean;
	/** The unit its shifts are filed under; null when not mapped. */
	unitName: string | null;
	/** For `matched_by_spelling`: the mapped locations it is a spelling of. */
	spellingOf: string[];
	/** For `not_mapped`: a unit for a human to confirm. Never applied by the run. */
	suggestedUnitName: string | null;
}

export interface SchedulePushNoLocationRow {
	shiftCount: number;
	status: SchedulePushLocationStatus;
	unitName: string | null;
}

export interface SchedulePushWindow {
	start: DateTime;
	end: DateTime;
}

/** Where Attendance on Demand files the department's shifts, read from its facility key (its Department ID). */
export interface SchedulePushWorkgroups {
	/** The organization's Attendance on Demand tenant (`elizajennings`). */
	tenant: string;
	/** The location code, the key's part before the slash (`12205`). */
	location: string;
	/** The business unit codes after it, sorted; empty when the key names the location alone. */
	businessUnits: string[];
	rule: SchedulePushWorkgroupRule;
}

export interface SchedulePushConfig {
	source: SchedulePushSource;
	/** The vendor as the backend names it. The panel prints `SCHEDULE_PUSH_VENDOR_NAMES` instead — see there. */
	vendorName: string;
	mappingMode: SchedulePushMappingMode;
	/** Null until the department's push is saved for the first time. */
	target: SchedulePushTarget | null;
	/** The facility code of the department's one active shifts integration with the vendor; what the panel fills in before the first save. */
	integrationFacilityCode: string | null;
	/** Without an active shifts integration the scheduled run never visits the department, so the switch cannot turn on. */
	integrationActive: boolean;
	facilityCodeEditable: boolean;
	hasPushRecords: boolean;
	loginStatus: SchedulePushLoginStatus;
	mapping: SchedulePushMapRow[];
	window: SchedulePushWindow;
	/** Empty when `mappingMode` is `none`. */
	locations: SchedulePushLocationRow[];
	/** Null when `mappingMode` is `none`: no shift is filed by its location. */
	noLocation: SchedulePushNoLocationRow | null;
	/**
	 * Attendance on Demand only: the saved key's workgroups, else the integration's key's. Null for a vendor with a
	 * location map, and when the key can't be read or the department has no single tenant. Read without asking the
	 * vendor; `vendor-units` checks a key live.
	 */
	workgroups: SchedulePushWorkgroups | null;
	/**
	 * Attendance on Demand only: how the push treats the department's organization, as engineering set it. Null for a
	 * vendor with no such policy, and when the organization can't be named or has none — sending can't be switched on then.
	 */
	pushPolicy: SchedulePushPolicy | null;
}

/** How Attendance on Demand's push treats one organization beyond where its shifts are filed. */
export interface SchedulePushPolicy {
	/** Whether a send states each shift's scheduled hours; when not, Attendance on Demand takes off its own meal break. */
	sendsScheduledMinutes: boolean;
	/** Whether a day someone entered in Attendance on Demand exactly as in InHouse becomes InHouse's, without a write. */
	takesOverMatchingDays: boolean;
}

export interface SaveSchedulePushConfigBody {
	facilityCode: string;
	/** The WHOLE map: a location left out is no longer mapped. */
	mapping: SchedulePushMapRow[];
}

export interface SchedulePushVendorUnit {
	/** What the map stores: the label, or the code when another unit of the facility shares the label. */
	name: string;
	/** The unit's label as people read it; its code when it has none. */
	label: string;
	code: string;
	labelShared: boolean;
}

export interface SchedulePushVendorUnits {
	facilityCode: string;
	/** Empty for a vendor without a location map. */
	units: SchedulePushVendorUnit[];
	/** Attendance on Demand only: the key's workgroups, each code checked live to name exactly one workgroup. */
	workgroups: SchedulePushWorkgroups | null;
}

/** The 422 a live check of an Attendance on Demand key answers with when a code names no workgroup, or several. */
export interface SchedulePushWorkgroupProblems {
	unknownWorkgroups: string[];
	ambiguousWorkgroups: string[];
}

export interface StartSchedulePushPreviewResponse {
	previewId: string;
}

/** One planned write in a preview's sample. */
export interface SchedulePushPlannedWrite {
	action: string;
	businessDate: DateTime | null;
	/** HostedTime's integer employee id; null for a vendor whose ids are codes (see `vendorEmployeeId`). */
	employeeOid: number | null;
	/** The vendor's employee id as text, for every vendor (Attendance on Demand's employee ID). */
	vendorEmployeeId: string | null;
	scheduleEventId: number | null;
	/** The vendor rows a whole-day write replaces or removes (Attendance on Demand); empty otherwise. */
	vendorRowIds: number[];
	plannedSignature: string | null;
	previousSignature: string | null;
}

/** The planner's action names, as `plannedByAction` keys them (`schedule_push/contract.py` `ActionType`). */
export type SchedulePushPlannedAction = "create" | "update" | "delete" | "takeover" | "mark_off_day" | "adopt_existing";

/**
 * For Attendance on Demand a write is one employee's whole day, and `unchanged` counts such days too;
 * `inWindowShifts` and `skippedByReason` count shifts for every vendor.
 */
export interface SchedulePushPreviewSummary {
	window: SchedulePushWindow | null;
	inWindowShifts: number;
	/** Writes the send would make, by action (`create`, `update`, `delete`, `takeover`, `mark_off_day`, `adopt_existing`). */
	plannedByAction: Record<string, number>;
	/** Shifts it would not send, by reason. */
	skippedByReason: Record<string, number>;
	unchanged: number;
	/** Days HostedTime holds several entries for one nurse, so nothing is written there. */
	ambiguousVendorDays: number;
	/** Shifts with no location filed under the no-location row's unit. */
	defaultedUnit: number;
	/** Up to a few planned writes of each action. */
	sample: SchedulePushPlannedWrite[];
}

export interface SchedulePushPreview {
	previewId: string;
	status: SchedulePushPreviewStatus;
	requestedBy: string;
	startedAt: DateTime;
	finishedAt: DateTime | null;
	/** Set when succeeded. */
	summary: SchedulePushPreviewSummary | null;
	csvAvailable: boolean;
	/** Set when failed or stale: the translation key to show, and the reason behind it. */
	errorKey: string | null;
	errorDetail: string | null;
}

/** The facility's units as the map's pickers use them. */
export type UnitOptions = {
	/** Picker options, or undefined while the facility's units can't be listed. */
	items: DropdownItem<string>[] | undefined;
	/** Case-folded names, for calling out a mapped name the facility no longer has; undefined until listed. */
	names: ReadonlySet<string> | undefined;
	loading: boolean;
	/** Why the pickers are read-only now; undefined when they can be used. */
	disabledReason: string | undefined;
};
